import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { LibrarySearchItem } from "../../../api/content/searchApi";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import SuggestionList from "../../../ui/forms/SuggestionList";
import { nextSuggestionIndex, suggestionOptionId } from "../../../ui/forms/suggestionNavigation";
import DropdownPopover from "../../../ui/overlays/DropdownPopover";
import LibraryHitDetail from "../library/LibraryHitDetail";
import LibrarySearchControls from "../library/LibrarySearchControls";
import { sideWithRoom } from "./pickerSide";
import { useLibrarySearch } from "../library/useLibrarySearch";
import { itemKindText } from "../../workflows/inputCards/typeWords";

interface LibraryPickerProps {
  projectId: string;
  /** The kinds this place takes, at least one. A constant: it keys the search. */
  kinds: readonly ItemKind[];
  /** The button's text: what to do, or the item chosen so far. */
  buttonLabel: string;
  /** The button's accessible name where its text alone is unclear, such as a chosen item's name. */
  buttonAccessibleName?: string;
  /** What is searched: the field's placeholder and the panel's name. */
  searchLabel: string;
  onPick: (item: LibrarySearchItem) => void;
  disabled?: boolean;
  align?: "left" | "right";
}

/**
 * Choosing a library item where it is used (F38; `docs/plans/artifact-search-in-place.md`): a
 * button opens a panel beside it, as GitHub's SelectPanel and VS Code's quick pick do, with
 * DMW's search syntax, a filter by kind or artifact type, and the items found. It is not a
 * modal: the page stays usable (F31). The field keeps the focus (WAI-ARIA APG combobox): Up
 * and Down move, Enter takes, Escape closes; a click outside closes it too. Nothing is chosen
 * until an item is taken. It reads the server's library search only.
 */
export default function LibraryPicker({ projectId, kinds, buttonLabel, buttonAccessibleName, searchLabel, onPick, disabled = false, align = "left" }: LibraryPickerProps) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<"below" | "above">("below");
  const panelId = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && anchor.current?.contains(event.target) !== true) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => { document.removeEventListener("mousedown", closeOutside); };
  }, [open]);
  const close = () => {
    setOpen(false);
    anchor.current?.querySelector("button")?.focus();
  };
  return (
    <span ref={anchor} className="app-dropdown-anchor inline-block">
      <AppButton
        variant="secondary"
        size="xs"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={buttonAccessibleName}
        disabled={disabled}
        onClick={() => {
          const box = anchor.current?.getBoundingClientRect();
          setSide(box === undefined ? "below" : sideWithRoom(box.top, box.bottom, window.innerHeight));
          setOpen((current) => !current);
        }}
      >
        {buttonLabel}
      </AppButton>
      <DropdownPopover open={open} align={align} width="lg" side={side}>
        <div id={panelId} role="group" aria-label={searchLabel} className="space-y-2 p-2">
          <PickerPanel projectId={projectId} kinds={kinds} searchLabel={searchLabel} onPick={(item) => { onPick(item); close(); }} onClose={close} />
        </div>
      </DropdownPopover>
    </span>
  );
}

const NO_ITEMS: readonly LibrarySearchItem[] = [];


/** The panel's content, mounted while open: each opening starts a fresh search. */
function PickerPanel({ projectId, kinds, searchLabel, onPick, onClose }: { projectId: string; kinds: readonly ItemKind[]; searchLabel: string; onPick: (item: LibrarySearchItem) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const search = useLibrarySearch(projectId, kinds, true);
  const items = search.results.status === "loaded" ? search.results.value : NO_ITEMS;
  const [activeIndex, setActiveIndex] = useState(0);
  const [shownItems, setShownItems] = useState(items);
  if (shownItems !== items) {
    setShownItems(items);
    setActiveIndex(0);
  }
  const active = Math.min(activeIndex, Math.max(items.length - 1, 0));
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const next = nextSuggestionIndex(event.key, active, items.length);
    const picked = items[active];
    if (next !== null) {
      setActiveIndex(next);
    } else if (event.key === "Enter") {
      // The field never submits a form around it: Enter only takes an item.
      if (picked !== undefined) onPick(picked);
    } else if (event.key === "Escape") {
      onClose();
    } else {
      return;
    }
    event.preventDefault();
  };
  return (
    <>
      <LibrarySearchControls
        search={search}
        label={searchLabel}
        combobox={{ listId, activeOptionId: items.length === 0 ? undefined : suggestionOptionId(listId, active), onKeyDown }}
      />
      {search.results.status !== "loaded" ? (
        <LoadStatus state={search.results} loadingLabel={t("search.searching")} onRetry={search.reload} compact={true} />
      ) : items.length === 0 ? (
        <EmptyState size="small">{t("search.noHits")}</EmptyState>
      ) : (
        <SuggestionList
          id={listId}
          label={searchLabel}
          items={items}
          activeIndex={active}
          itemKey={(item) => item.reference}
          renderItem={(item) => <PickerRow item={item} showsKind={kinds.length !== 1} />}
          onChoose={onPick}
          placement="inPanel"
        />
      )}
    </>
  );
}

function PickerRow({ item, showsKind }: { item: LibrarySearchItem; showsKind: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="block min-w-0 space-y-0.5">
      <span className="flex min-w-0 flex-wrap items-center gap-1">
        <span className="min-w-0 truncate font-medium">{item.name}</span>
        {showsKind ? <Chip size="compact">{itemKindText(item.kind, t)}</Chip> : null}
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-1 text-xs [overflow-wrap:anywhere]">
        <LibraryHitDetail item={item} />
      </span>
    </span>
  );
}
